import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleStackIcon,
  ClockIcon,
  DocumentTextIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { formatRelativeTime } from "@utils/formatRelativeTime";
import { getReportLifecycleStatus } from "@utils/reportStatus";
import { formatDate } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { Alert, Button, Headline, Paragraph, Small, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportTypeChoices,
  ReportReviewView,
  ReportType,
  ReportTypeEdge,
  useGetProviderLearnersQuery,
  useGetRecentlyViewedReportsQuery,
  useGetReportsQuery,
  useReportReviewCountsQuery,
} from "@api/graphql/types-and-hooks";
import { ClinicalNewReportDialog } from "@components/ClinicalNewReportDialog/ClinicalNewReportDialog";
import { ClinicalReport } from "@components/ClinicalReportsTable/ClinicalReportsTable";
import { EhrSyncGatedNotice } from "@components/EHRConnect/EhrSyncGatedNotice";
import { StatusDefinitionTooltip } from "@components/StatusLegendTooltip/StatusDefinitionTooltip";
import { StatusTag } from "@components/StatusTag/StatusTag";
import { useClientListImport } from "@hooks/useClientListImport";
import { useCreateReportHandler } from "@hooks/useCreateReportHandler";
import { useHomeAnalytics } from "@hooks/useHomeAnalytics";
import { useRecentlyViewedReports } from "@hooks/useRecentlyViewedReports";
import { HomeState, isHomeEhrConnected, selectHomeState } from "@pages/HomePage/selectHomeState";
import { isStatusMaskedOrgType } from "@pages/Onboarding/ehrConnectionState";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";
import {
  computeReportTypeFilter,
  getClinicalReportBasePath,
  getClinicalReportPath,
  mapReports,
} from "@routes/ClinicalReports/ClinicalReports";
import { getReviewListPath } from "@routes/ClinicalReports/reviewFilter";

import guideInitialAssessment from "../../assets/guides/initial-assessment.jpg";
import guideReassessment from "../../assets/guides/reassessment.jpg";
import guideSourceDocuments from "../../assets/guides/source-documents.jpg";

// The homepage state is normally derived from the BCBA's real signals (EHR mode,
// connection, clients, report history) via selectHomeState. `?state=` overrides
// it for design/QA review of any variant from any account (mirrors onboarding's
// `?step=`/`?flow=`). The flow is launched for BCBAs behind the `portal-home-page`
// LaunchDarkly flag (route + redirect gating live in router.tsx / layouts).
const VALID_STATES: HomeState[] = ["1a", "1b", "2", "3"];

const REPORT_TYPE_LABEL: Partial<Record<ApiReportReportTypeChoices, string>> = {
  [ApiReportReportTypeChoices.InitialAssessment]: "Initial Assessment",
  [ApiReportReportTypeChoices.Reassessment]: "Reassessment",
  [ApiReportReportTypeChoices.DiagnosisEvaluation]: "Diagnosis Evaluation",
};

const reportTypeLabel = (t: ApiReportReportTypeChoices): string => REPORT_TYPE_LABEL[t] ?? "Report";

export const HomePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const flags = useFlags<FeatureFlags>();
  const { roles, organizationType, orgnizationDetailsQuery, loginProviderDetailsQuery, isEhrSyncCleared } =
    useAdminData();
  const {
    isEHREnabled,
    isEHRStatusLoading,
    ehrConnectionStatus,
    isOrgUsernamePassword,
    isCRDataWarehouse,
    hasCredentials,
    openEHRLogin,
    usesOrgSession,
  } = useEHRConnection();

  const hasBcbaRole = roles.includes("bcba") || roles.includes("assessment-bcba");
  const hasPsychologistRole = roles.includes("psychologist") || roles.includes("evaluation-psychologist");
  const reportTypeFilter = computeReportTypeFilter(flags, hasBcbaRole, hasPsychologistRole);

  // `getReports` is scoped server-side to the signed-in user via `createdById`.
  // We wait for the login-provider query to resolve before enabling the reports
  // query so the user's own ID is ready to pass as the filter — this keeps
  // `totalCount` accurate (user's own total, not the org's) and avoids exposing
  // other BCBAs' reports or client names. refetchOnWindowFocus is enabled so a
  // second tab that's already on /home picks up EHR / import changes via focus.
  const currentUserId = loginProviderDetailsQuery.data?.getLoginProvider?.auth0User?.id;
  const reportsQuery = useGetReportsQuery(
    { first: 10, filter: { reportType: reportTypeFilter, sortBy: "-last_updated_date", createdById: currentUserId } },
    {
      queryKey: ["home-reports", reportTypeFilter, currentUserId],
      retry: false,
      refetchOnWindowFocus: true,
      enabled: !!currentUserId,
    },
  );
  const learnersQuery = useGetProviderLearnersQuery(
    { learnerName: "" },
    { queryKey: ["home-provider-learners"], retry: false, refetchOnWindowFocus: true },
  );

  // Server already scopes edges to the current user via createdById; guard on
  // currentUserId so we never render a colleague's reports while the ID resolves.
  const reports: ClinicalReport[] = React.useMemo(() => {
    if (!currentUserId) return [];
    const edges = (reportsQuery.data?.getReports?.edges ?? []).filter(Boolean) as ReportTypeEdge[];
    return mapReports(edges);
  }, [reportsQuery.data, currentUserId]);

  // totalCount is now the user's own report total (server-side createdById filter),
  // so hasReports correctly reflects whether this BCBA has created any reports.
  const totalReports = reportsQuery.data?.getReports?.totalCount ?? 0;
  const hasReports = totalReports > 0;
  const hasClients = (learnersQuery.data?.getProviderLearners?.totalCount ?? 0) > 0;

  const {
    captureHomeViewed,
    captureStartReportClicked,
    captureConnectEhrClicked,
    captureImportClientsClicked,
    captureReportClicked,
  } = useHomeAnalytics();

  // Start-a-report flow, reused from the Clinical Reports page.
  const reportHandler = useCreateReportHandler();
  const startReport = () => {
    if (state) captureStartReportClicked(state);
    reportHandler.setNewReportDialogOpen(true);
  };

  // Opening a report records it in the per-user "Recently Viewed" history (same
  // store the Clinical Reports table writes to) and navigates to its full route.
  // mapReports only carries the lifecycle leaf in `redirectRoute`, so we resolve
  // the absolute path via getClinicalReportPath rather than navigating to the
  // bare leaf.
  const { recordView } = useRecentlyViewedReports();
  const openReport = (r: ClinicalReport) => {
    const path = getClinicalReportPath(r);
    captureReportClicked({
      source: "your_reports",
      report_type: r.reportType,
      report_status: r.status,
      list_position: reports.findIndex((x) => x.id === r.id),
    });
    recordView(r.id);
    navigate(path);
  };

  // `?state=` QA override bypasses the loading guard so any variant is reviewable
  // instantly from any account.
  const rawState = searchParams.get("state")?.toLowerCase();
  const overrideState = (VALID_STATES as string[]).includes(rawState ?? "") ? (rawState as HomeState) : null;

  // Hold until the signals that drive the branch have settled (success OR error,
  // so a failed query never hangs the page on the loader). The reports query is
  // gated on currentUserId (enabled: !!currentUserId), so it starts once the
  // login-provider resolves. We gate on `isSuccess || isError` (not `isLoading`)
  // so we block both the initial fetch AND the brief pending/idle window right
  // after the query becomes enabled (where isLoading is false but data hasn't
  // arrived). Crucially, isSuccess stays true through background refetches from
  // refetchOnWindowFocus, so window-focus refetches never re-show the skeleton.
  //
  // When currentUserId is absent we block on !isFetching rather than settling
  // immediately — this covers the stale-cache scenario where cached
  // getLoginProvider data predates the auth0User field (isLoading=false but
  // auth0User.id is missing while a background refetch is in flight).
  const reportsSettled = currentUserId
    ? reportsQuery.isSuccess || reportsQuery.isError
    : !loginProviderDetailsQuery.isFetching;
  const isReady =
    reportsSettled &&
    !learnersQuery.isLoading &&
    !isEHRStatusLoading &&
    !loginProviderDetailsQuery.isLoading &&
    (orgnizationDetailsQuery.isSuccess || orgnizationDetailsQuery.isError);

  const computed = selectHomeState({
    hasReports,
    hasClients,
    isEHREnabled,
    organizationType,
    ehrConnectionStatus,
    isOrgUsernamePassword,
    isCRDataWarehouse,
    hasCredentials,
  });
  // Fall back to the safe Ready state (2) when the routing signals can't be
  // trusted — "Start a report" works for everyone and EHR connect/import stay
  // reachable from the persistent header EHR badge. Both fallbacks apply ONLY to a
  // computed cold-start (1a/1b): state 3 is driven by hasReports (a trustworthy
  // successful reports fetch) and must never be downgraded by an unrelated signal
  // failure. Two such cases:
  //  - Org details errored → organizationType / EHR mode unknown, so 1a/1b-vs-2
  //    is guesswork; don't infer a no-EHR org from missing data and strand an
  //    EHR/direct-integration BCBA on the wrong state.
  //  - Reports/learners errored while we computed a cold-start → hasReports/
  //    hasClients defaulted to 0, so "no reports/no clients" is untrustworthy and
  //    we must not drop an established provider into a cold-start.
  const isColdStart = computed === "1a" || computed === "1b";
  const cantTrustOrg = orgnizationDetailsQuery.isError && isColdStart;
  const cantTrustHistory = (reportsQuery.isError || learnersQuery.isError) && isColdStart;
  const resolved: HomeState = cantTrustOrg || cantTrustHistory ? "2" : computed;
  const state: HomeState | null = overrideState ?? (isReady ? resolved : null);

  // Step 3 (Import) of the setup stepper. Reuses the onboarding import
  // orchestration (start + status polling). Only relevant in 1b (connected, no
  // clients) — Hipp Health's backend status is still hardcoded CONNECTED and
  // never transitions through IMPORTING_CLIENT_LIST, so it skips polling; all
  // other org types (including Passage Health and Therapz, PROA-2515) poll for
  // real status. On success we refetch provider learners: hasClients flips
  // true → selectHomeState moves to "2" ("Ready when you are / Start a new
  // report"), kicking the BCBA into a report.
  const { isImporting, startImport } = useClientListImport({
    // INT-36: CentralReach orgs on shared credentials queue the import
    // on the org-wide session instead of signalling a per-provider
    // workflow, and completion is read off the request row.
    usesOrgSession,
    enabled: state === "1b",
    waitForStatusTransition: !isStatusMaskedOrgType(organizationType),
    isEhrSyncCleared,
    onSuccess: () => {
      void learnersQuery.refetch();
    },
  });

  // Top-of-funnel: which state a BCBA actually lands in. Fires once the real
  // state resolves; skips the `?state=` QA override so design/QA review doesn't
  // pollute the data. (lastViewedState in the hook dedupes within a mount.)
  React.useEffect(() => {
    if (overrideState || !state) return;
    captureHomeViewed(state, hasReports, hasClients);
  }, [overrideState, state, hasReports, hasClients, captureHomeViewed]);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-6 py-8">
      <GreetingHeader />
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          {state === null && <HomeLoading />}
          {(state === "1a" || state === "1b") && (
            // 1a (not connected) and 1b (connected, no clients) are the same
            // setup stepper at different points: 1a's active step is Sync EHR,
            // 1b's is Import. All step states derive from the signals below.
            // openEHRLogin's 2nd arg = true routes through the "Reaching out to
            // your EHR" surface → consent → full-bleed login page (matching the
            // onboarding Connect step) instead of the bare credential form.
            <EHRSetupSteps
              onConnectEHR={() => {
                if (state) captureConnectEhrClicked(state);
                openEHRLogin(undefined, true, true);
              }}
              // Use the same "connected" notion selectHomeState used to reach 1b, so
              // direct-integration / stored-credential users don't get stuck on an
              // incomplete Step 2 with Import disabled.
              ehrConnected={isHomeEhrConnected({ organizationType, ehrConnectionStatus, hasCredentials })}
              hasClients={hasClients}
              isImporting={!!isImporting}
              onImport={() => {
                if (state) captureImportClientsClicked(state);
                startImport();
              }}
              isEhrSyncCleared={isEhrSyncCleared}
            />
          )}
          {state === "2" && <ReadyNoReports onStartReport={startReport} />}
          {state === "3" && (
            <Active reports={reports} totalCount={totalReports} onStartReport={startReport} onOpenReport={openReport} />
          )}
        </div>
        <aside className="col-span-12 space-y-6 lg:col-span-4">
          <ReviewPromptLinks />
          <WhatsNewPanel />
          {/* Recently viewed is its own concern from the main widget: it can hold
              other BCBAs' reports the user opened, while "Your Reports" is the
              user's own. Shown once setup is done (state 2 onward). */}
          {(state === "2" || state === "3") && <RecentlyViewedReportsWidget />}
        </aside>
      </div>

      <ClinicalNewReportDialog
        isNewReportDialogOpen={reportHandler.isNewReportDialogOpen}
        setNewReportDialogOpen={(open) => reportHandler.setNewReportDialogOpen(open)}
        selectedReportType={reportHandler.selectedReportType}
        setSelectedReportType={reportHandler.setSelectedReportType}
        selectedClientId={reportHandler.selectedClientId}
        setSelectedClientId={reportHandler.setSelectedClientId}
        selectedTemplate={reportHandler.selectedTemplate}
        setSelectedTemplate={reportHandler.setSelectedTemplate}
        getClientReport={reportHandler.getClientReport}
        createReportObject={reportHandler.createReportObject}
        goToReport={reportHandler.goToReport}
        isReportCreating={reportHandler.isReportCreating}
        learnerName={reportHandler.learnerName}
        setLearnerName={reportHandler.setLearnerName}
        learnerQuery={reportHandler.learnerQuery}
      />
    </div>
  );
};

// Team Review shortcuts at the top of the sidebar, above What's New (every home state): each opens the reports
// page on its review filter and is hidden at zero. Off entirely when Team Review is off.
const ReviewPromptLinks: React.FC = () => {
  const navigate = useNavigate();
  // useIsPeerReviewEnabled also requires a per-report-type QA scanner flag read from report context, which
  // doesn't exist on home. Home gates on the flag plus the organization's Team Review setting.
  const flags = useFlags<FeatureFlags>();
  const { isOrgPeerReviewEnabled } = useAdminData();
  const isPeerReviewEnabled = !!flags["report-peer-review"] && isOrgPeerReviewEnabled;
  const { data, error, refetch, isFetching } = useReportReviewCountsQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: true,
    enabled: isPeerReviewEnabled,
  });
  // Report each failure streak once; the alert below tells the user.
  const loggedErrorRef = React.useRef(false);
  React.useEffect(() => {
    if (!error) {
      loggedErrorRef.current = false;
    } else if (!loggedErrorRef.current) {
      loggedErrorRef.current = true;
      datadogRum.addError(error, { context: "Home review counts failed" });
    }
  }, [error]);
  if (!isPeerReviewEnabled) return null;
  if (error) {
    return (
      <Alert
        appearance="error"
        title="Couldn't load review counts"
        text={
          <div className="flex w-full items-center gap-3">
            <p className="flex-1">Your review shortcuts are unavailable.</p>
            <Button
              text="Retry"
              variant="secondary"
              buttonStyle="border"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            />
          </div>
        }
      />
    );
  }

  const waiting = data?.reportReviewCounts?.waitingOnMe ?? 0;
  const sentBack = data?.reportReviewCounts?.sentBackToMe ?? 0;
  const rows = [
    {
      key: ReportReviewView.WaitingOnMe,
      count: waiting,
      label: `${waiting} ${waiting === 1 ? "report" : "reports"} waiting on you`,
      badge: "bg-blue-100 text-blue-700",
      row: "border-blue-200 bg-blue-50",
    },
    {
      key: ReportReviewView.SentBackToMe,
      count: sentBack,
      label: `${sentBack} ${sentBack === 1 ? "report" : "reports"} sent back to you`,
      badge: "bg-yellow-100 text-yellow-800",
      row: "border-yellow-200 bg-yellow-50",
    },
  ].filter((r) => r.count > 0);
  if (rows.length === 0) return null;

  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => navigate(getReviewListPath(r.key))}
          className={twMerge("flex w-full items-center gap-3 rounded-lg border py-2 pl-3 pr-4 text-left", r.row)}
        >
          <span
            className={twMerge(
              "flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-semibold",
              r.badge,
            )}
          >
            {r.count}
          </span>
          <span className="flex-1 text-sm font-semibold text-text-primary">{r.label}</span>
          <span className="text-sm text-text-link">View →</span>
        </button>
      ))}
    </div>
  );
};

const getTimeOfDay = (date: Date = new Date()): "morning" | "afternoon" | "evening" => {
  const hour = date.getHours();
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
};

const GreetingHeader: React.FC = () => {
  const { user } = useAuth0();
  const firstName = user?.given_name ?? user?.name?.split(" ")[0] ?? "there";
  const timeOfDay = getTimeOfDay();

  return (
    <div className="mb-6">
      <Headline size="medium">
        Good {timeOfDay}, {firstName}.
      </Headline>
    </div>
  );
};

// Neutral placeholder while the branch-driving signals settle — avoids flashing
// a cold-start (1a/1b) before reports/clients/org data resolve.
const HomeLoading: React.FC = () => (
  <div className="space-y-4 rounded-lg border border-border-strong bg-white p-6" aria-busy="true">
    <div className="h-5 w-1/3 animate-pulse rounded bg-limestone-200" />
    <div className="h-2.5 w-full animate-pulse rounded-full bg-limestone-200" />
    <div className="h-2.5 w-5/6 animate-pulse rounded-full bg-limestone-200" />
    <div className="h-2.5 w-2/3 animate-pulse rounded-full bg-limestone-200" />
  </div>
);

// ---------- Shared building blocks ----------

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <p className={twMerge("text-[11px] font-semibold uppercase tracking-[0.08em] text-text-tertiary", className)}>
    {children}
  </p>
);

// "Next steps for you" — a stepper card that reiterates the BCBA's onboarding
// steps (profile → connect/sync EHR → import clients) on the homepage when the
// flow was skipped. Each step renders a "Completed" pill, an in-progress status,
// or an action button; later steps stay disabled until the prior one is done
// (sequential gating). Data-driven so the same card serves the role/EHR
// permutations (e.g. 1a's active step is Sync EHR, 1b's is Import).
interface NextStep {
  /** Full row title, e.g. "Step 2: Sync your EHR". */
  title: string;
  description: string;
  icon: React.ReactNode;
  /** Renders the green "Completed" pill (wins over inProgress/action). */
  completed?: boolean;
  /** Renders a spinner + label (e.g. "Importing…"), matching the onboarding step. */
  inProgress?: boolean;
  inProgressLabel?: string;
  /** The action button shown when the step is neither completed nor in progress. */
  action?: { label: string; onClick?: () => void; disabled?: boolean };
  /** Inline copy shown below the description, e.g. the managed-onboarding gate notice. */
  extraNotice?: React.ReactNode;
}

const NextStepStatus: React.FC<{ step: NextStep }> = ({ step }) => {
  if (step.completed) {
    return (
      <span className="flex items-center gap-1 rounded-md bg-limestone-200 px-1.5 py-1">
        <CheckIcon className="h-4 w-4 text-green-700" />
        <span className="text-xs text-text-secondary">Completed</span>
      </span>
    );
  }
  if (step.inProgress) {
    // Mirrors the onboarding ImportClientsStep "Importing…" treatment.
    return (
      <span className="flex items-center gap-1.5 text-sm text-text-tertiary">
        <ArrowPathIcon className="h-4 w-4 animate-slow-spin" />
        {step.inProgressLabel ?? "Importing…"}
      </span>
    );
  }
  if (step.action) {
    return (
      <Button
        text={step.action.label}
        variant="primary"
        buttonStyle="filled"
        size="xs"
        disabled={step.action.disabled}
        onClick={step.action.onClick}
      />
    );
  }
  return null;
};

const NextStepRow: React.FC<{ step: NextStep }> = ({ step }) => (
  <div className="flex w-full items-center gap-2 rounded-lg border border-border-medium py-2 pl-2 pr-4">
    <div className="flex min-w-0 flex-1 items-start gap-3 p-1">
      <span className="flex shrink-0 items-center justify-center rounded-md bg-limestone-50 p-2 text-icon-secondary">
        {step.icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-text-primary">{step.title}</p>
        <Small className="mt-0.5 block text-text-secondary">{step.description}</Small>
        {step.extraNotice}
      </div>
    </div>
    <div className="shrink-0">
      <NextStepStatus step={step} />
    </div>
  </div>
);

const NextStepsCard: React.FC<{ steps: NextStep[] }> = ({ steps }) => (
  <div className="rounded-lg border border-border-strong bg-white px-4 pb-6 pt-4">
    <Headline size="extra-small">Next steps for you</Headline>
    <Paragraph size="small" className="mt-1 text-text-secondary">
      Follow the steps below to complete your account setup.
    </Paragraph>
    <div className="mt-4 space-y-2">
      {steps.map((step, i) => (
        // Gentle staggered entrance as the card replaces the loading skeleton —
        // reuses onboarding's slide-up vocabulary; fill-mode:both holds the rows
        // hidden until their turn so nothing flashes in early.
        <div
          key={step.title}
          className="animate-onboarding-step-in [animation-fill-mode:both]"
          style={{ animationDelay: `${i * 70}ms` }}
        >
          <NextStepRow step={step} />
        </div>
      ))}
    </div>
  </div>
);

// Quick tips shown in the cold-start states — what the BCBA is working toward
// while they finish setup. Flipped through in the carousel below. Each completes
// the card's "Setting up your account lets you…" lead-in; `lead` renders bold.
const BUILDING_TOWARD_TIPS: { lead: string; body: string }[] = [
  {
    lead: "Create polished, payor-ready reports",
    body: "your assessments, observations, and clinical narrative, compiled and ready to review, sign, and send.",
  },
  {
    lead: "Skip the manual entry",
    body: "once your EHR is connected, your clients and their data sync automatically, keeping your reports in step with your system of record.",
  },
  {
    lead: "Start from a draft, not a blank page",
    body: "Frontera builds each section from your chosen template, so you're refining instead of writing from scratch.",
  },
];

// How long to dwell on a tip before auto-advancing: a fixed read-in buffer plus
// time proportional to the tip's length at an unhurried ~200 wpm, clamped so no
// tip flips before it can be read or lingers too long.
const TIP_READING_MS_PER_WORD = 300; // ~200 words per minute
const TIP_BASE_MS = 2000;
const TIP_MIN_MS = 5000;
const TIP_MAX_MS = 12000;
const tipDurationMs = (tip: { lead: string; body: string }): number => {
  const words = `${tip.lead} ${tip.body}`.trim().split(/\s+/).length;
  return Math.min(TIP_MAX_MS, Math.max(TIP_MIN_MS, TIP_BASE_MS + words * TIP_READING_MS_PER_WORD));
};

// Educational anchor used by the cold-start states: a small carousel of quick
// tips in the same dashed-card visual identity, so the BCBA can flip through
// them while their setup is still in progress.
const WhatYoureBuildingTowardCard: React.FC = () => {
  const [index, setIndex] = React.useState(0);
  // Track travel direction so the incoming tip slides in from the side that
  // matches the flip: "next" enters from the right, "prev" from the left.
  const [direction, setDirection] = React.useState<1 | -1>(1);
  // Auto-advance pauses while the user is hovering OR keyboard-focused inside the
  // card, so it never flips a tip out from under someone reading or interacting.
  // Hover and focus are tracked separately (and OR'd) so clearing one — e.g. the
  // pointer leaving while a tip control still has focus — can't resume playback.
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocusWithin, setIsFocusWithin] = React.useState(false);
  const isPaused = isHovered || isFocusWithin;
  const count = BUILDING_TOWARD_TIPS.length;

  // Respect the OS "reduce motion" setting: skip auto-advance entirely (the
  // manual controls still work). Guarded for non-browser/jsdom environments.
  const prefersReducedMotion =
    typeof globalThis.matchMedia === "function" && globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Auto-advance to the next tip after a reading-rate-based dwell. Re-armed on
  // every index change (manual or automatic) so the timer always reflects the
  // tip currently on screen; cleared while paused or under reduced motion.
  React.useEffect(() => {
    if (isPaused || prefersReducedMotion || count <= 1) return;
    const id = globalThis.setTimeout(() => {
      setDirection(1);
      setIndex((i) => (i + 1) % count);
    }, tipDurationMs(BUILDING_TOWARD_TIPS[index]));
    return () => globalThis.clearTimeout(id);
  }, [index, isPaused, prefersReducedMotion, count]);

  const paginate = (dir: 1 | -1) => {
    setDirection(dir);
    setIndex((i) => (i + dir + count) % count);
  };
  const jumpTo = (target: number) => {
    if (target === index) return;
    setDirection(target > index ? 1 : -1);
    setIndex(target);
  };

  // Snappy press feedback + a high-contrast (black) focus ring per the
  // design-craft accessibility guidance; controls are ≥32px touch targets.
  const controlClasses =
    "flex h-8 w-8 items-center justify-center rounded-md text-icon-secondary transition-colors hover:bg-limestone-200 hover:text-text-primary active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-black";

  return (
    <div
      className="rounded-lg border border-dashed border-border-medium bg-limestone-50 p-6"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocusWithin(true)}
      // onBlur bubbles on focus-out, so only clear focus-pause when focus actually
      // leaves the card — not when tabbing between its own tip dots / chevrons.
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsFocusWithin(false);
      }}
    >
      <Eyebrow>Setting up your account lets you&hellip;</Eyebrow>
      <div className="mt-2 min-h-[88px] overflow-hidden">
        {/* keyed on the tip's lead (stable, unique) so each tip remounts and
            re-triggers the slide-in when the active tip changes */}
        <Paragraph
          key={BUILDING_TOWARD_TIPS[index].lead}
          size="small"
          className={twMerge("text-text-secondary", direction === 1 ? "animate-tip-in-right" : "animate-tip-in-left")}
        >
          <span className="font-semibold text-text-primary">{BUILDING_TOWARD_TIPS[index].lead}</span> —{" "}
          {BUILDING_TOWARD_TIPS[index].body}
        </Paragraph>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center" role="tablist" aria-label="Tips">
          {BUILDING_TOWARD_TIPS.map((tip, i) => (
            <button
              key={tip.lead}
              type="button"
              role="tab"
              aria-label={`Tip ${i + 1} of ${count}`}
              aria-selected={i === index}
              onClick={() => jumpTo(i)}
              className="group flex items-center rounded px-1 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
            >
              {/* small visual bar, generous invisible hit area around it */}
              <span
                className={twMerge(
                  "h-1.5 rounded-full transition-all duration-200 ease-out",
                  i === index ? "w-4 bg-text-secondary" : "w-1.5 bg-limestone-400 group-hover:bg-limestone-500",
                )}
              />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous tip" onClick={() => paginate(-1)} className={controlClasses}>
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button type="button" aria-label="Next tip" onClick={() => paginate(1)} className={controlClasses}>
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Right column ----------

// Trailing path segment of a support-hub URL (".../support-hub/jun-8-12" →
// "jun-8-12"), used as a safe-to-capture analytics slug — it's our own marketing
// content, never PHI. Plain splits (no regex) drop the query, fragment, and any
// trailing slash.
const supportHubSlug = (url: string): string => {
  const segments = url.split("?")[0].split("#")[0].split("/").filter(Boolean);
  return segments[segments.length - 1] ?? url;
};

// Public HubSpot help-center release notes. Static list for now — a live feed
// requires a backend endpoint holding a read-only KB token (the frontend can't
// call HubSpot directly: the credential can't ship client-side and HubSpot's
// APIs don't send CORS headers for our origin). Each entry links to its weekly
// release-notes article in the support hub; keep newest first.

interface NewsItem {
  title: string;
  body: string;
  /** HubSpot help-center article for this release. */
  url: string;
}

const WHATS_NEW: NewsItem[] = [
  {
    title: "August 31–September 6 release notes",
    body: "Clear error messages for password-protected PDFs, improved AI report content, a searchable subtype picker, and fixes for learner search and Export crashes.",
    url: "https://49169539.hs-sites.com/support-hub/week-of-august-31-september-6-2026",
  },
  {
    title: "August 24–30 release notes",
    body: 'A new "My Reports" filter and creator column in Clinical Reports, improved AI-generated content across report sections, and clearer upload error messages.',
    url: "https://49169539.hs-sites.com/support-hub/week-of-august-24-30-2026",
  },
  {
    title: "August 15–23 release notes",
    body: "CFQL-2 and BHI assessment support, insurance-matched templates, a duplicate-reassessment warning, a redesigned client creation form, and visible QA suggestion counts on targets.",
    url: "https://49169539.hs-sites.com/support-hub/-",
  },
  {
    title: "August 10–14 release notes",
    body: "Reassessment progress charts and clinician edits now survive data re-imports, automatic chart refresh after EHR refresh, reliable assessment uploads, and sturdier exports.",
    url: "https://49169539.hs-sites.com/support-hub/week-of-august-10-14-2026",
  },
];

const WhatsNewPanel: React.FC = () => {
  const { captureReleaseNotesClicked } = useHomeAnalytics();
  const navigate = useNavigate();
  return (
    <div className="rounded-lg border border-border-strong bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">What&apos;s New</h2>
        <a
          href="/release-notes"
          onClick={(e) => {
            e.preventDefault();
            captureReleaseNotesClicked("view_all", -1);
            navigate("/release-notes");
          }}
          className="text-xs font-medium text-interactive-primary-default underline"
        >
          View all release notes
        </a>
      </div>
      <ul className="space-y-4">
        {WHATS_NEW.map((item, index) => (
          <li key={item.url}>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => captureReleaseNotesClicked(supportHubSlug(item.url), index)}
              className="text-sm font-medium text-interactive-primary-default underline"
            >
              {item.title}
            </a>
            <Small className="mt-0.5 block text-text-tertiary">{item.body}</Small>
          </li>
        ))}
      </ul>
    </div>
  );
};

// "Recently Viewed Reports" — reports the signed-in user has opened, newest
// first. Only report ids + view times are persisted per user/org; the widget
// re-fetches just the fields it renders (client name, type, status) through
// `getReports` (reportIds filter). Because that query stays org- and role-scoped,
// an id from another org — or one the user is no longer allowed to see — simply
// resolves to nothing, so cross-org PHI can never render. Falls back to an empty
// state until the user has opened something.
const RecentlyViewedReportsWidget: React.FC = () => {
  const navigate = useNavigate();
  const { captureReportClicked } = useHomeAnalytics();
  const { recentlyViewed, recordView } = useRecentlyViewedReports();

  const reportIds = React.useMemo(() => recentlyViewed.map((entry) => entry.id), [recentlyViewed]);
  const { data } = useGetRecentlyViewedReportsQuery({ reportIds }, { enabled: reportIds.length > 0 });

  // Build the display rows straight from the minimal query, re-ordered to the
  // local view history with each entry's viewedAt. Ids the backend didn't return
  // — deleted, or (by design) belonging to another org — simply drop out. We
  // navigate to the report's base path and let its route resolve the right step,
  // so no section/file/goal data is needed here.
  const items = React.useMemo(() => {
    const byId = new Map(
      (data?.getReports?.edges ?? []).map((edge) => edge?.node).flatMap((node) => (node ? [[node.id, node]] : [])),
    );
    return recentlyViewed
      .flatMap((entry) => {
        const node = byId.get(entry.id);
        if (!node) return [];
        const first = node.reportClientDetails?.clientFirstName || "N/A";
        const last = node.reportClientDetails?.clientLastName || "";
        return [
          {
            id: node.id,
            clientName: `${first} ${last}`.trim(),
            reportType: node.reportType,
            status: getReportLifecycleStatus(node as ReportType),
            path: getClinicalReportBasePath({ id: node.id, reportType: node.reportType }),
            viewedAt: entry.viewedAt,
          },
        ];
      })
      .slice(0, 4);
  }, [data, recentlyViewed]);

  // Re-opening bumps the entry to the front (refresh viewedAt), same as opening
  // from "Your Reports" or the reports table.
  const openReport = (item: (typeof items)[number], listPosition: number) => {
    captureReportClicked({
      source: "recently_viewed",
      report_type: item.reportType,
      report_status: item.status,
      list_position: listPosition,
    });
    recordView(item.id);
    navigate(item.path);
  };

  return (
    <div className="rounded-lg border border-border-strong bg-white p-5">
      <h2 className="mb-3 text-sm font-semibold text-text-primary">Recently Viewed Reports</h2>
      {items.length === 0 ? (
        <div className="flex flex-col items-center rounded-lg border border-dashed border-border-medium px-4 py-6 text-center">
          <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-limestone-100">
            <ClockIcon className="h-5 w-5 text-icon-secondary" />
          </div>
          <p className="text-sm font-medium text-text-primary">No reports viewed yet</p>
          <Small className="mt-1 block text-text-tertiary">Reports you open will show up here for quick access.</Small>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => openReport(item, index)}
                className="w-full rounded-lg border border-border-medium p-3 text-left transition-colors hover:bg-limestone-50"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-limestone-100">
                    <DocumentTextIcon className="h-4 w-4 text-icon-secondary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text-primary ph-no-capture">{item.clientName}</p>
                    <Small className="block truncate text-text-tertiary">{reportTypeLabel(item.reportType)}</Small>
                    <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-text-tertiary">
                      <ClockIcon className="h-3.5 w-3.5" />
                      <span>Viewed {formatRelativeTime(new Date(item.viewedAt))}</span>
                      <span aria-hidden="true">·</span>
                      <span>{item.status}</span>
                    </div>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------- States 1A + 1B: EHR setup stepper (connect, then import) ----------

interface EHRSetupStepsProps {
  onConnectEHR: () => void;
  ehrConnected: boolean;
  hasClients: boolean;
  isImporting: boolean;
  onImport: () => void;
  isEhrSyncCleared: boolean;
}

// Shown for a BCBA on an EHR org who skipped onboarding and isn't fully set up:
//   1a (not connected) → Step 2 "Sync EHR" is the active step, Import disabled.
//   1b (connected, no clients) → Step 2 done, Step 3 "Import" is the active step.
// Every step's state derives from the signals below, so one component covers
// both. Profile is always complete (it can't be skipped in onboarding). When
// Import succeeds, hasClients flips and selectHomeState advances off this view
// to state 2 ("Ready when you are / Start a new report").
const EHRSetupSteps: React.FC<EHRSetupStepsProps> = ({
  onConnectEHR,
  ehrConnected,
  hasClients,
  isImporting,
  onImport,
  isEhrSyncCleared,
}) => {
  const gatedNotice = !isEhrSyncCleared ? <EhrSyncGatedNotice /> : undefined;
  return (
    <div className="space-y-6">
      <NextStepsCard
        steps={[
          {
            title: "Step 1: Profile Details",
            description: "Tell us how you'd like to be addressed across Frontera.",
            icon: <UserIcon className="h-6 w-6" />,
            completed: true,
          },
          {
            // Flips to "Completed" once the EHR connection lands (status in
            // CONNECTED_STATUSES) — i.e. the user returns from the connect surface.
            title: "Step 2: Sync your EHR",
            description: "Connect your EHR so Frontera can securely sync your clients and their data.",
            icon: <ArrowsRightLeftIcon className="h-6 w-6" />,
            completed: ehrConnected,
            action: { label: "Sync EHR", onClick: onConnectEHR, disabled: !isEhrSyncCleared },
            extraNotice: !ehrConnected ? gatedNotice : undefined,
          },
          {
            // Disabled until the EHR is connected; shows "Importing…" while the
            // bulk import runs, then "Completed" once clients land.
            title: "Step 3: Import your clients",
            description: "Import your clients so their data is ready when you start a report.",
            icon: <CircleStackIcon className="h-6 w-6" />,
            completed: hasClients,
            inProgress: isImporting,
            inProgressLabel: "Importing…",
            action: { label: "Import", onClick: onImport, disabled: !ehrConnected || !isEhrSyncCleared },
            extraNotice: ehrConnected ? gatedNotice : undefined,
          },
        ]}
      />
      <WhatYoureBuildingTowardCard />
    </div>
  );
};

interface Guide {
  title: string;
  description: string;
  image: string;
  /** HubSpot help-center article (same support hub as the What's New panel). */
  url: string;
}

const GUIDES: Guide[] = [
  {
    title: "Step-by-step: Creating Initial Assessment Report",
    description:
      "A walkthrough of the full initial assessment workflow, from selecting your client to reviewing and exporting your finished report.",
    image: guideInitialAssessment,
    url: "https://49169539.hs-sites.com/support-hub/sbs-ia",
  },
  {
    title: "Step-by-step: Reassessment",
    description:
      "A step-by-step guide to starting, building, and submitting a reassessment report — including uploading your previous report, reviewing goal progress, and signing off.",
    image: guideReassessment,
    url: "https://49169539.hs-sites.com/support-hub/step-by-step-reassessment",
  },
  {
    title: "Assessment Builder Overview",
    description:
      "An overview of the Assessment Builder — covering how to create reports, select and save goals, use Frontera templates, and get the most out of the platform.",
    image: guideSourceDocuments,
    url: "https://49169539.hs-sites.com/support-hub/assessment-builder-overview",
  },
];

const GuidesSection: React.FC = () => {
  const { captureGuideClicked } = useHomeAnalytics();
  return (
    <section>
      <Headline size="small">Guides</Headline>
      <Paragraph size="small" className="mt-1 text-text-secondary">
        Learn the basics of starting your first report.
      </Paragraph>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {GUIDES.map((g, index) => (
          <a
            key={g.title}
            href={g.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => captureGuideClicked(supportHubSlug(g.url), index)}
            className="group block overflow-hidden rounded-lg border border-border-medium bg-white transition-shadow hover:shadow-md"
          >
            <img src={g.image} alt="" className="aspect-[4/3] w-full object-cover" />
            <div className="p-4">
              <p className="text-sm font-semibold text-text-primary group-hover:underline">{g.title}</p>
              <Small className="mt-1 block text-text-tertiary">{g.description}</Small>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
};

// ---------- State 2: Ready but waiting ----------

// Set up + ready, but no reports yet: a centered empty state with a single
// "Start new report" CTA, then the guides.
const ReadyNoReports: React.FC<{ onStartReport: () => void }> = ({ onStartReport }) => (
  <div className="space-y-8">
    <div className="rounded-lg border border-border-strong bg-white p-8">
      <div className="flex flex-col items-center text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-limestone-100">
          <DocumentTextIcon className="h-7 w-7 text-icon-secondary" />
        </div>
        <Headline size="small">No reports yet.</Headline>
        <Paragraph size="small" className="mt-2 max-w-md text-text-secondary">
          Reports you create will appear here.
        </Paragraph>
        <div className="mt-6">
          <Button text="Start new report" variant="primary" buttonStyle="filled" onClick={onStartReport} />
        </div>
      </div>
    </div>
    <GuidesSection />
  </div>
);

// ---------- State 3: Active — the BCBA's own reports across all clients ----------

const YourReportsWidget: React.FC<{
  reports: ClinicalReport[];
  totalCount: number;
  onStartReport: () => void;
  onOpenReport: (r: ClinicalReport) => void;
}> = ({ reports, totalCount, onStartReport, onOpenReport }) => {
  let subtitle = "Your most recently created reports.";
  if (reports.length > 0) {
    subtitle = totalCount === 1 ? "1 report" : `${totalCount} reports`;
  }
  return (
    <div className="rounded-lg border border-border-strong bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-border-medium px-6 py-4">
        <div className="min-w-0">
          <p className="text-base font-semibold text-text-primary">Your Reports</p>
          <Small className="mt-0.5 block truncate text-text-tertiary">{subtitle}</Small>
        </div>
        <Button text="Start new report" variant="primary" buttonStyle="filled" onClick={onStartReport} />
      </div>
      {reports.length === 0 ? (
        <div className="flex flex-col items-center px-6 py-10 text-center">
          <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-limestone-100">
            <DocumentTextIcon className="h-5 w-5 text-icon-secondary" />
          </div>
          <p className="text-sm font-medium text-text-primary">No reports created by you yet</p>
          <Small className="mt-1 block text-text-tertiary">Reports you create will appear here.</Small>
        </div>
      ) : (
        <ul className="divide-y divide-border-medium">
          {reports.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onOpenReport(r)}
                className="flex w-full items-center gap-4 px-6 py-3 text-left transition-colors hover:bg-limestone-50"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-limestone-100">
                  <DocumentTextIcon className="h-5 w-5 text-icon-secondary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-text-primary ph-no-capture">{r.clientName}</p>
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-text-tertiary">
                    <span className="truncate">{reportTypeLabel(r.reportType)}</span>
                    <span aria-hidden="true">·</span>
                    <Tooltip placement="bottom" showInfoIcon={false} text="Date this report was created">
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {formatDate(r.dateCreated)}
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <StatusDefinitionTooltip status={r.status}>
                  <StatusTag status={r.status} />
                </StatusDefinitionTooltip>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="border-t border-border-medium px-6 py-3 text-center">
        <a href="/clinical-reports" className="text-sm font-medium text-interactive-primary-default underline">
          View all reports
        </a>
      </div>
    </div>
  );
};

const Active: React.FC<{
  reports: ClinicalReport[];
  totalCount: number;
  onStartReport: () => void;
  onOpenReport: (r: ClinicalReport) => void;
}> = ({ reports, totalCount, onStartReport, onOpenReport }) => (
  <div className="space-y-8">
    <YourReportsWidget
      reports={reports}
      totalCount={totalCount}
      onStartReport={onStartReport}
      onOpenReport={onOpenReport}
    />
    <GuidesSection />
  </div>
);
